import { FC, MouseEvent, SyntheticEvent, useState } from 'react';
import { filter, find, includes, remove } from 'lodash';

import { UnderlinedButton } from '@components/shared/buttons/underlined-button/UnderlinedButton';
import { Collapse } from '@components/shared/collapse/Collapse';
import { ExpandPanel } from '@components/shared/ExpandPanel/ExpandPanel';
import { Checkbox } from '@components/shared/form-elements/checkbox/Checkbox';
import { IFilterOptions, IFilterProp, ISetFiltersParam, ProductAttributesDictionary } from '@types';
import { ATTRIBUTE_LENGTH_PATH } from './constants';
import FilterLabel from './FilterLabel';
import { MAX_OPTIONS_SHOWN, sortNumericByValue } from './options-tools';

interface IProps {
  filters: ISetFiltersParam;
  setFilters: (newFilters: IFilterProp[]) => void;
  label: string;
  filterOptionsData: IFilterOptions;
  classes: { [key: string]: string };
}

const LengthOptions: FC<IProps> = ({ classes, label: labelSection, filterOptionsData, setFilters, filters }) => {
  const [allList, setAllList] = useState(false);
  const attributeOptions = find(filterOptionsData.attributes, { code: ProductAttributesDictionary.LENGTH });
  const attributeFilterValues: string[] =
    filter(filters.arrayRes, { path: ATTRIBUTE_LENGTH_PATH }).map((filter) => filter.value) || [];

  if (!attributeOptions || (Array.isArray(attributeOptions.options) && !attributeOptions.options.length)) {
    return null;
  }

  const filtersFast = filters.arrayRes || [];

  const handleLengthFilterChange = (key: number, isChecked: boolean) => {
    const newFilterPropValue: IFilterProp = {
      rule: 'include',
      path: ATTRIBUTE_LENGTH_PATH,
      value: `${key}`
    };
    if (!isChecked) {
      remove(filtersFast, newFilterPropValue);
    } else if (!includes(filtersFast, newFilterPropValue)) {
      filtersFast.push(newFilterPropValue);
    }
    setFilters(filtersFast);
  };

  const arraySelected = filtersFast.filter((item) => item.path === ATTRIBUTE_LENGTH_PATH);
  const arraySelectedLength = (arraySelected && arraySelected.length) || 0;
  const arraySelectedLast = arraySelectedLength ? arraySelected[0] : null;

  const handleResetLengthFilters = (ev: SyntheticEvent | MouseEvent) => {
    if (arraySelectedLast) {
      ev.stopPropagation();
      setFilters(filtersFast.filter((item) => item.path !== ATTRIBUTE_LENGTH_PATH));
    }
  };

  const allAttributeOptions = attributeOptions.options.map((o) => ({
    value: Number(o.value?.replaceAll('-', '')),
    label: `${o.label} см`,
    isChecked: attributeFilterValues.length > 0 && attributeFilterValues.includes(o.value?.replaceAll('-', ''))
  }));

  const uncollapsedOptionsNodes = allAttributeOptions
    .filter((o, idx) => o.isChecked || idx < MAX_OPTIONS_SHOWN)
    .sort(sortNumericByValue)
    .map(({ isChecked, label, value }) => (
      <Checkbox
        checked={isChecked}
        label={label}
        key={value}
        onChange={(_event: SyntheticEvent, checked: boolean) => handleLengthFilterChange(value, checked)}
        name={label}
      />
    ));

  const allAttributeOptionsNodes = allAttributeOptions
    .sort(sortNumericByValue)
    .map(({ isChecked, label, value }) => (
      <Checkbox
        checked={isChecked}
        label={label}
        key={value}
        onChange={(_event: SyntheticEvent, checked: boolean) => handleLengthFilterChange(value, checked)}
        name={label}
      />
    ));

  const toggleAllList = () => setAllList((state) => !state);

  const labelComp = (
    <FilterLabel
      classes={classes}
      label={labelSection}
      arraySelectedLast={arraySelectedLast}
      arraySelectedLength={arraySelectedLength}
      handleReset={handleResetLengthFilters}
    />
  );

  return (
    <ExpandPanel defaultExpanded={true} label={labelComp} className={classes.paddedExpandPanel}>
      {!allList ? <Collapse in={!allList}>{uncollapsedOptionsNodes}</Collapse> : null}
      <Collapse in={allList}>
        <div className={classes.expandedList}>{allAttributeOptionsNodes}</div>
      </Collapse>
      {allAttributeOptionsNodes && allAttributeOptionsNodes.length > MAX_OPTIONS_SHOWN ? (
        <UnderlinedButton
          className={classes.collapseAllList}
          onClick={toggleAllList}
          label={`${allList ? 'Згорнути' : 'Розгорнути'} (${
            allAttributeOptionsNodes.length + (allList ? 0 : 1) - uncollapsedOptionsNodes.length
          })`}
        />
      ) : null}
    </ExpandPanel>
  );
};

export default LengthOptions;
